// ============================================================
// 天水數位店長 — API Client
// 封裝 fetch，自動附帶 JWT token 與錯誤處理
// ============================================================

const BASE_URL = '/api';

/**
 * 單次請求逾時（毫秒）。
 * Render 免費方案閒置後會冷啟動，第一個請求可能要 30–60 秒。
 * 沒有逾時的話畫面會無限停在「載入中」，員工只好強制關閉再開，
 * 反而讓情況更糟。設 45 秒並自動重試一次，涵蓋冷啟動又不會卡太久。
 */
const REQUEST_TIMEOUT_MS = 45_000;

/** 伺服器可能正在冷啟動時，供畫面顯示提示用 */
export const SLOW_REQUEST_HINT_MS = 3_000;

interface ApiClientOptions {
  /** 憑證存在 localStorage 的哪個 key */
  storageKey?: string;
  /** 收到 401 時要做什麼（預設：清掉憑證並導向登入頁） */
  onUnauthorized?: () => void;
}

class ApiClient {
  private token: string | null = null;
  private readonly storageKey: string;
  private readonly onUnauthorized: () => void;

  constructor(opts: ApiClientOptions = {}) {
    this.storageKey = opts.storageKey ?? 'ts_token';
    this.onUnauthorized = opts.onUnauthorized ?? (() => { window.location.href = '/login'; });
  }

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem(this.storageKey, token);
    } else {
      localStorage.removeItem(this.storageKey);
    }
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem(this.storageKey);
    }
    return this.token;
  }

  /**
   * 帶逾時的 fetch；逾時或網路錯誤時自動重試一次
   * （只重試 GET——非 GET 重送可能造成重複打卡）。
   */
  private async fetchWithTimeout(url: string, init: RequestInit): Promise<Response> {
    const isGet = (init.method ?? 'GET') === 'GET';
    const attempts = isGet ? 2 : 1;
    let lastErr: unknown;

    for (let i = 0; i < attempts; i++) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      try {
        return await fetch(url, { ...init, signal: controller.signal });
      } catch (err) {
        lastErr = err;
      } finally {
        clearTimeout(timer);
      }
    }

    if (lastErr instanceof DOMException && lastErr.name === 'AbortError') {
      throw new Error('伺服器沒有回應（可能正在喚醒），請稍後再試一次');
    }
    throw new Error('連線不到伺服器，請確認網路後再試');
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const res = await this.fetchWithTimeout(`${BASE_URL}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    // 登入端點本身的 401 代表「帳密不對 / 被鎖 / 未設定」，
    // 不是 session 過期 —— 不可清 token、不可跳轉，要讓登入頁顯示真正原因。
    const isLoginAttempt = path === '/pin-auth/login' || path === '/auth/login';
    if (res.status === 401 && !isLoginAttempt) {
      this.setToken(null);
      this.onUnauthorized();
      throw new Error('登入已過期，請重新登入');
    }

    // 安全解析 JSON — 防止空回應導致 crash
    const text = await res.text();
    let data: unknown;
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      throw new Error(`伺服器回應格式錯誤 (${res.status})`);
    }

    if (!res.ok) {
      const errObj = data as Record<string, string>;
      throw new Error(errObj?.error ?? `Request failed: ${res.status}`);
    }

    return data as T;
  }

  get<T>(path: string) {
    return this.request<T>('GET', path);
  }

  post<T>(path: string, body?: unknown) {
    return this.request<T>('POST', path, body);
  }

  put<T>(path: string, body?: unknown) {
    return this.request<T>('PUT', path, body);
  }

  delete<T>(path: string) {
    return this.request<T>('DELETE', path);
  }
}

export const api = new ApiClient();

/**
 * 店內打卡站專用。不能共用上面的 api：
 * - 共用裝置上可能殘留某位員工的 ts_token，會被一起帶出去
 * - 打卡站憑證失效時不該被導向登入頁，而是回到「輸入啟用碼」畫面
 */
export const kioskApi = new ApiClient({
  storageKey: 'ts_kiosk_token',
  onUnauthorized: () => { /* KioskPage 偵測到沒有憑證就會顯示啟用畫面 */ },
});
